import { useEffect, useMemo } from "react";
import { FieldProps, RJSFSchema } from "@rjsf/utils";
import { get } from "lodash";
import { shuffleArray } from "common/utils/arrays";
import MultiSelect, { BaseOption } from "../../Inputs/MultiSelect";
import { Input } from "../__styles__/Inputs";

// Type for oneOf options with const and title from RJSFSchema
// In JSON Schema, oneOf is an array of schemas, and for MultiSelect we use schemas with const and title
type OneOfOption = RJSFSchema & { const: string; title: string };

export const MultiSelectInput = (props: FieldProps) => {
  const disabled = props.disabled || props.readonly;
  const isRequired = props.required && !disabled;
  const rawValue = props.formData;
  const randomizeOptionOrder = props.uiSchema?.["ui:randomizeOptionOrder"];

  useEffect(() => {
    if (isRequired && Array.isArray(rawValue) && rawValue.length === 0) {
      props.onChange(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = Array.isArray(rawValue) ? rawValue : (rawValue ?? []);

  const oneOfOptions = get(props.schema, "items.oneOf") as
    | OneOfOption[]
    | undefined;
  const options: BaseOption[] = useMemo(() => {
    let formattedOptions: BaseOption[] = [];

    if (oneOfOptions) {
      formattedOptions = oneOfOptions
        .map((option: OneOfOption): BaseOption | null => {
          if (typeof option === "object" && option.const && option.title) {
            return { value: option.const, label: option.title };
          }
          return null;
        })
        .filter(
          (option: BaseOption | null): option is BaseOption => option !== null,
        );
    }

    if (randomizeOptionOrder) {
      formattedOptions = shuffleArray(formattedOptions);
    }

    return formattedOptions;
  }, [oneOfOptions, randomizeOptionOrder]);

  const handleChange = (newValue: string[] | BaseOption[] | null) => {
    if (
      isRequired &&
      (!newValue || (Array.isArray(newValue) && newValue.length === 0))
    ) {
      props.onChange(undefined);
    } else {
      props.onChange(newValue);
    }
  };

  return (
    <Input
      id={props.idSchema.$id}
      isSubmissionBuilder={props.isSubmissionBuilder}
    >
      <MultiSelect
        value={value}
        onChange={handleChange}
        options={options}
        name={props.name}
        required={isRequired}
        label={props.schema.title}
        error={props.rawErrors?.join(", ")}
        disabled={disabled}
        tooltip={props.uiSchema?.["ui:tooltipText"]}
        helperText={props.uiSchema?.["ui:helperText"]}
        placeholder={props.uiSchema?.["ui:placeholder"]}
      />
    </Input>
  );
};
